// Procedural geometry builder. Every vertex carries its own material so whole
// props (and whole levels) can be merged into a single draw call.
// Layout: pos(3) normal(3) color(3) mat(4: roughness, metalness, emissive, reflectivity)
import { mat4, col } from './math.js';

export const STRIDE = 13;

/** Material helper: m('#ff8800', {rough:.4, metal:0, emit:0, refl:0}) */
export function m(color, o = {}) {
  return {
    color: typeof color === 'string' || typeof color === 'number' ? col(color) : color,
    rough: o.rough ?? 0.55,
    metal: o.metal ?? 0,
    emit: o.emit ?? 0,
    refl: o.refl ?? 0,
  };
}

const tmpM = mat4.create();

export class GeoBuilder {
  constructor() {
    this.v = [];
    this.i = [];
  }
  get vertexCount() {
    return this.v.length / STRIDE;
  }
  _xf(t) {
    t = t || {};
    const p = t.p || [0, 0, 0], r = t.r || [0, 0, 0];
    const s = t.s === undefined ? [1, 1, 1] : typeof t.s === 'number' ? [t.s, t.s, t.s] : t.s;
    mat4.compose(tmpM, p, r, [1, 1, 1]);
    return { M: new Float32Array(tmpM), s };
  }
  /** push raw vertex list (local), applying transform */
  pushVerts(pos, nor, idx, mat, t, colors) {
    const { M, s } = this._xf(t);
    const base = this.vertexCount;
    const c = mat.color;
    for (let k = 0; k < pos.length; k += 3) {
      const px = pos[k] * s[0], py = pos[k + 1] * s[1], pz = pos[k + 2] * s[2];
      let nx = nor[k] / s[0], ny = nor[k + 1] / s[1], nz = nor[k + 2] / s[2];
      const wx = M[0] * px + M[4] * py + M[8] * pz + M[12];
      const wy = M[1] * px + M[5] * py + M[9] * pz + M[13];
      const wz = M[2] * px + M[6] * py + M[10] * pz + M[14];
      let tx = M[0] * nx + M[4] * ny + M[8] * nz;
      let ty = M[1] * nx + M[5] * ny + M[9] * nz;
      let tz = M[2] * nx + M[6] * ny + M[10] * nz;
      const l = Math.hypot(tx, ty, tz) || 1;
      const cc = colors ? colors[k / 3] : c;
      this.v.push(wx, wy, wz, tx / l, ty / l, tz / l, cc[0], cc[1], cc[2], mat.rough, mat.metal, mat.emit, mat.refl);
    }
    // negative determinant scale flips winding
    const flip = s[0] * s[1] * s[2] < 0;
    for (let k = 0; k < idx.length; k += 3) {
      if (flip) this.i.push(base + idx[k], base + idx[k + 2], base + idx[k + 1]);
      else this.i.push(base + idx[k], base + idx[k + 1], base + idx[k + 2]);
    }
    return this;
  }
  merge(other, t) {
    // merge another builder with transform (keeps materials)
    const { M, s } = this._xf(t);
    const base = this.vertexCount;
    const v = other.v;
    for (let k = 0; k < v.length; k += STRIDE) {
      const px = v[k] * s[0], py = v[k + 1] * s[1], pz = v[k + 2] * s[2];
      const nx = v[k + 3] / s[0], ny = v[k + 4] / s[1], nz = v[k + 5] / s[2];
      const tx = M[0] * nx + M[4] * ny + M[8] * nz;
      const ty = M[1] * nx + M[5] * ny + M[9] * nz;
      const tz = M[2] * nx + M[6] * ny + M[10] * nz;
      const l = Math.hypot(tx, ty, tz) || 1;
      this.v.push(
        M[0] * px + M[4] * py + M[8] * pz + M[12],
        M[1] * px + M[5] * py + M[9] * pz + M[13],
        M[2] * px + M[6] * py + M[10] * pz + M[14],
        tx / l, ty / l, tz / l,
        v[k + 6], v[k + 7], v[k + 8], v[k + 9], v[k + 10], v[k + 11], v[k + 12]
      );
    }
    for (const ix of other.i) this.i.push(base + ix);
    return this;
  }

  box(w, h, d, mat, t) {
    const x = w / 2, y = h / 2, z = d / 2;
    const P = [], N = [], I = [];
    const face = (n, u, v) => {
      const b = P.length / 3;
      const c = [n[0] * x, n[1] * y, n[2] * z];
      const U = [u[0] * x, u[1] * y, u[2] * z], V = [v[0] * x, v[1] * y, v[2] * z];
      for (const [a, bb] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        P.push(c[0] + U[0] * a + V[0] * bb, c[1] + U[1] * a + V[1] * bb, c[2] + U[2] * a + V[2] * bb);
        N.push(...n);
      }
      I.push(b, b + 1, b + 2, b, b + 2, b + 3);
    };
    face([0, 0, 1], [1, 0, 0], [0, 1, 0]);
    face([0, 0, -1], [-1, 0, 0], [0, 1, 0]);
    face([1, 0, 0], [0, 0, -1], [0, 1, 0]);
    face([-1, 0, 0], [0, 0, 1], [0, 1, 0]);
    face([0, 1, 0], [1, 0, 0], [0, 0, -1]);
    face([0, -1, 0], [1, 0, 0], [0, 0, 1]);
    return this.pushVerts(P, N, I, mat, t);
  }

  roundBox(w, h, d, r, mat, t, seg = 3) {
    const hx = w / 2, hy = h / 2, hz = d / 2;
    r = Math.min(r, hx, hy, hz);
    const coords = (hh) => {
      const a = [];
      for (let i = 0; i <= seg; i++) a.push(-hh + (r * i) / seg);
      for (let i = 0; i <= seg; i++) a.push(hh - r + (r * i) / seg);
      return a;
    };
    const X = coords(hx), Y = coords(hy), Z = coords(hz);
    const P = [], N = [], I = [];
    const push = (px, py, pz) => {
      const ix = Math.max(-(hx - r), Math.min(hx - r, px));
      const iy = Math.max(-(hy - r), Math.min(hy - r, py));
      const iz = Math.max(-(hz - r), Math.min(hz - r, pz));
      let nx = px - ix, ny = py - iy, nz = pz - iz;
      const l = Math.hypot(nx, ny, nz) || 1;
      nx /= l; ny /= l; nz /= l;
      P.push(ix + nx * r, iy + ny * r, iz + nz * r);
      N.push(nx, ny, nz);
    };
    const grid = (A, B, fn, flip) => {
      const b = P.length / 3;
      for (let j = 0; j < B.length; j++) for (let i = 0; i < A.length; i++) fn(A[i], B[j]);
      const n = A.length;
      for (let j = 0; j < B.length - 1; j++)
        for (let i = 0; i < n - 1; i++) {
          const a = b + j * n + i, c = a + n;
          if (flip) I.push(a, c, a + 1, a + 1, c, c + 1);
          else I.push(a, a + 1, c, a + 1, c + 1, c);
        }
    };
    grid(X, Y, (a, b) => push(a, b, hz), false);
    grid(X, Y, (a, b) => push(a, b, -hz), true);
    grid(Z, Y, (a, b) => push(hx, b, a), true);
    grid(Z, Y, (a, b) => push(-hx, b, a), false);
    grid(X, Z, (a, b) => push(a, hy, b), true);
    grid(X, Z, (a, b) => push(a, -hy, b), false);
    return this.pushVerts(P, N, I, mat, t);
  }

  /** Lathe: profile = [[radius, y], ...] from bottom to top */
  lathe(profile, seg, mat, t, colors) {
    const P = [], N = [], I = [];
    const n = profile.length;
    // compute profile normals
    const pn = profile.map((p, i) => {
      const a = profile[Math.max(0, i - 1)], b = profile[Math.min(n - 1, i + 1)];
      const dr = b[0] - a[0], dy = b[1] - a[1];
      const l = Math.hypot(dr, dy) || 1;
      return [dy / l, -dr / l];
    });
    for (let s = 0; s <= seg; s++) {
      const th = (s / seg) * Math.PI * 2;
      const c = Math.cos(th), si = Math.sin(th);
      for (let i = 0; i < n; i++) {
        const [r, y] = profile[i];
        P.push(r * si, y, r * c);
        N.push(pn[i][0] * si, pn[i][1], pn[i][0] * c);
      }
    }
    for (let s = 0; s < seg; s++)
      for (let i = 0; i < n - 1; i++) {
        const a = s * n + i, b = (s + 1) * n + i;
        I.push(a, b, a + 1, a + 1, b, b + 1);
      }
    let cols;
    if (colors) {
      cols = [];
      for (let s = 0; s <= seg; s++) for (let i = 0; i < n; i++) cols.push(colors[i]);
    }
    return this.pushVerts(P, N, I, mat, t, cols);
  }

  cylinder(rt, rb, h, mat, t, seg = 16, caps = true) {
    const y0 = -h / 2, y1 = h / 2;
    // hard edge: separate side & caps
    this.lathe([[rb, y0], [rt, y1]], seg, mat, t);
    if (caps) {
      this.disc(rt, mat, { ...(t || {}), ...compose(t, [0, y1, 0], false) }, seg);
      this.disc(rb, mat, compose(t, [0, y0, 0], true), seg);
    }
    return this;
  }

  disc(r, mat, t, seg = 16) {
    const P = [0, 0, 0], N = [0, 1, 0], I = [];
    for (let s = 0; s <= seg; s++) {
      const th = (s / seg) * Math.PI * 2;
      P.push(Math.sin(th) * r, 0, Math.cos(th) * r);
      N.push(0, 1, 0);
      if (s > 0) I.push(0, s, s + 1);
    }
    return this.pushVerts(P, N, I, mat, t);
  }

  sphere(r, mat, t, ws = 16, hs = 12) {
    const prof = [];
    for (let i = 0; i <= hs; i++) {
      const a = -Math.PI / 2 + (i / hs) * Math.PI;
      prof.push([Math.cos(a) * r, Math.sin(a) * r]);
    }
    prof[0][0] = 0.0001;
    prof[hs][0] = 0.0001;
    // lathe normals from profile are fine for spheres
    return this.lathe(prof, ws, mat, t);
  }

  hemisphere(r, mat, t, ws = 16, hs = 8) {
    const prof = [];
    for (let i = 0; i <= hs; i++) {
      const a = (i / hs) * Math.PI / 2;
      prof.push([Math.cos(a) * r, Math.sin(a) * r]);
    }
    prof[hs][0] = 0.0001;
    return this.lathe(prof, ws, mat, t);
  }

  capsule(r, h, mat, t, ws = 14, hs = 6) {
    const prof = [];
    for (let i = 0; i <= hs; i++) {
      const a = -Math.PI / 2 + (i / hs) * (Math.PI / 2);
      prof.push([Math.cos(a) * r, Math.sin(a) * r - h / 2]);
    }
    for (let i = 0; i <= hs; i++) {
      const a = (i / hs) * (Math.PI / 2);
      prof.push([Math.cos(a) * r, Math.sin(a) * r + h / 2]);
    }
    prof[0][0] = 0.0001;
    prof[prof.length - 1][0] = 0.0001;
    return this.lathe(prof, ws, mat, t);
  }

  cone(r, h, mat, t, seg = 14) {
    return this.lathe([[0.0001, -h / 2], [r, -h / 2], [0.0001, h / 2]], seg, mat, t);
  }

  torus(R, r, mat, t, seg = 24, tube = 10, arc = Math.PI * 2) {
    const P = [], N = [], I = [];
    for (let s = 0; s <= seg; s++) {
      const u = (s / seg) * arc;
      const cu = Math.cos(u), su = Math.sin(u);
      for (let k = 0; k <= tube; k++) {
        const v = (k / tube) * Math.PI * 2;
        const cv = Math.cos(v), sv = Math.sin(v);
        P.push((R + r * cv) * cu, r * sv, (R + r * cv) * su);
        N.push(cv * cu, sv, cv * su);
      }
    }
    const n = tube + 1;
    for (let s = 0; s < seg; s++)
      for (let k = 0; k < tube; k++) {
        const a = s * n + k, b = (s + 1) * n + k;
        I.push(a, a + 1, b, a + 1, b + 1, b);
      }
    return this.pushVerts(P, N, I, mat, t);
  }

  plane(w, d, mat, t, nx = 1, nz = 1) {
    const P = [], N = [], I = [];
    for (let j = 0; j <= nz; j++)
      for (let i = 0; i <= nx; i++) {
        P.push(-w / 2 + (w * i) / nx, 0, -d / 2 + (d * j) / nz);
        N.push(0, 1, 0);
      }
    for (let j = 0; j < nz; j++)
      for (let i = 0; i < nx; i++) {
        const a = j * (nx + 1) + i, c = a + nx + 1;
        I.push(a, c, a + 1, a + 1, c, c + 1);
      }
    return this.pushVerts(P, N, I, mat, t);
  }

  /**
   * Sweep a circle along a polyline. `radii` is a number or per-point array.
   * `colors` optional per-point [r,g,b] (linear) to vary colour along the tube. Ends are capped.
   * `flat` squashes the cross-section on the frame's second axis (1 = round).
   */
  tube(pts, radii, mat, sides = 10, colors = null, flat = 1) {
    const n = pts.length;
    const R = (i) => (typeof radii === 'number' ? radii : radii[i]);
    const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
    const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    const norm = (a) => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
    const T = pts.map((p, i) => norm(sub(pts[Math.min(n - 1, i + 1)], pts[Math.max(0, i - 1)])));
    let up = Math.abs(T[0][1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    let Nn = norm(cross(cross(T[0], up), T[0]));
    const P = [], NN = [], I = [], C = [];
    for (let i = 0; i < n; i++) {
      if (i > 0) {
        // parallel transport
        const b = cross(T[i - 1], T[i]);
        if (Math.hypot(b[0], b[1], b[2]) > 1e-6) Nn = norm(cross(T[i], cross(Nn, T[i])));
      }
      const B = norm(cross(T[i], Nn));
      const r = R(i);
      for (let s = 0; s <= sides; s++) {
        const a = (s / sides) * Math.PI * 2;
        const ca = Math.cos(a), sa = Math.sin(a) * flat;
        const d = [Nn[0] * ca + B[0] * sa, Nn[1] * ca + B[1] * sa, Nn[2] * ca + B[2] * sa];
        const dn = norm([Nn[0] * ca + B[0] * Math.sin(a) / flat, Nn[1] * ca + B[1] * Math.sin(a) / flat, Nn[2] * ca + B[2] * Math.sin(a) / flat]);
        P.push(pts[i][0] + d[0] * r, pts[i][1] + d[1] * r, pts[i][2] + d[2] * r);
        NN.push(dn[0], dn[1], dn[2]);
        C.push(colors ? colors[i] : mat.color);
      }
    }
    const w = sides + 1;
    for (let i = 0; i < n - 1; i++)
      for (let s = 0; s < sides; s++) {
        const a = i * w + s, c = (i + 1) * w + s;
        I.push(a, a + 1, c, a + 1, c + 1, c);
      }
    // caps
    for (const [i, sgn] of [[0, -1], [n - 1, 1]]) {
      const base = P.length / 3;
      P.push(pts[i][0], pts[i][1], pts[i][2]);
      NN.push(T[i][0] * sgn, T[i][1] * sgn, T[i][2] * sgn);
      C.push(colors ? colors[i] : mat.color);
      for (let s = 0; s < sides; s++) {
        if (sgn < 0) I.push(base, i * w + s + 1, i * w + s);
        else I.push(base, i * w + s, i * w + s + 1);
      }
    }
    return this.pushVerts(P, NN, I, mat, null, colors ? C : null);
  }

  /** Leaf-like flattened ellipsoid */
  leaf(len, wid, mat, t) {
    return this.sphere(1, mat, { ...(t || {}), s: [wid, wid * 0.25, len] }, 10, 6);
  }
}

// helper to offset a transform by local position (used by cylinder caps)
function compose(t, off, flipDown) {
  t = t || {};
  const p = t.p || [0, 0, 0], r = t.r || [0, 0, 0];
  const s = t.s === undefined ? [1, 1, 1] : typeof t.s === 'number' ? [t.s, t.s, t.s] : t.s;
  const M = mat4.compose(mat4.create(), p, r, [1, 1, 1]);
  const lp = mat4.transformPoint(M, [off[0] * s[0], off[1] * s[1], off[2] * s[2]]);
  return { p: lp, r: flipDown ? r : r, s: flipDown ? [s[0], -s[1], s[2]] : s };
}
